/**
 * Built-in Stanford Karel lessons (units 1-12). Each unit registers an ordered
 * list of lesson factories, replacing the legacy global `UnitNLessonM`
 * discovery convention.
 */
import {
  ButtonLesson,
  IdeDemo,
  IdeLesson,
  PictureDemo,
  PictureLesson,
} from "../learn/engine/lessonsModel";
import {
  registerUnit,
  type LessonFactory,
  type UnitSnippets,
} from "../learn/engine/lessonRegistry";

const SNIPPETS: Record<string, UnitSnippets> = {
  "meet-karel": {
    js: `// Meet Karel
// Karel • Beeper
// World • Wall`,
    python: `# Meet Karel
# Karel • Beeper
# World • Wall`,
  },
  commands: {
    js: `move();
turnLeft();
putBeeper();
pickBeeper();`,
    python: `move()
turn_left()
put_beeper()
pick_beeper()`,
  },
  programs: {
    js: `function main() {
  move();
  putBeeper();
}`,
    python: `def main():
    move()
    put_beeper()`,
  },
  functions: {
    js: `function turnAround() {
  turnRight();
  turnRight();
}`,
    python: `def turn_around():
    turn_right()
    turn_right()`,
  },
  newspaper: {
    js: `function main() {
  moveToNewspaper();
  returnHome();
}`,
    python: `def main():
    move_to_newspaper()
    return_home()`,
  },
  repeat: {
    js: `repeat(4) {
  move();
  putBeeper();
}`,
    python: `repeat(4):
    move()
    put_beeper()`,
  },
  conditionals: {
    js: `frontIsClear()
beepersPresent()
facingNorth()`,
    python: `front_is_clear()
beepers_present()
facing_north()`,
  },
  while: {
    js: `while (frontIsClear()) {
  move();
}`,
    python: `while front_is_clear():
    move()`,
  },
  if: {
    js: `if (beepersPresent()) {
  pickBeeper();
} else putBeeper();`,
    python: `if beepers_present():
    pick_beeper()
else:
    put_beeper()`,
  },
  color: {
    js: `paintCorner('red');
move();
paintCorner('blue');`,
    python: `paint_corner('red')
move()
paint_corner('blue')`,
  },
  random: {
    js: `if (random()) {
  turnLeft();
} else move();`,
    python: `if random():
    turn_left()
else:
    move()`,
  },
  midpoint: {
    js: `placeLineOfBeepers();
while (beepersPresent()) {
  pickBeeper();
  turnAround();
}`,
    python: `place_line_of_beepers()
while beepers_present():
    pick_beeper()
    turn_around()`,
  },
};

export function registerBuiltinLessons(): void {
  registerUnit("Meet Karel", [u1l1, u1l2], SNIPPETS["meet-karel"]);
  registerUnit(
    "Commands",
    [u2l1, u2l2, u2l3, u2l4, u2l5, u2l6, u2l7, u2l8],
    SNIPPETS.commands,
  );
  registerUnit(
    "Programs",
    [u3l1, u3l2, u3l3, u3l4, u3l5, u3l6],
    SNIPPETS.programs,
  );
  registerUnit("Functions", [u4l1, u4l2], SNIPPETS.functions);
  registerUnit("Newspaper", [u5l1], SNIPPETS.newspaper);
  registerUnit("Repeat", [u6l1, u6l2], SNIPPETS.repeat);
  registerUnit("Conditionals", [u7l1, u7l2, u7l3], SNIPPETS.conditionals);
  registerUnit("While", [u8l1, u8l2, u8l3, u8l4, u8l5], SNIPPETS.while);
  registerUnit("If", [u9l1, u9l2], SNIPPETS.if);
  registerUnit("Color", [u10l1, u10l2], SNIPPETS.color);
  registerUnit("Random", [u11l1, u11l2], SNIPPETS.random);
  registerUnit("Midpoint", [u12l1], SNIPPETS.midpoint);
}

const karelConcepts = () => [
  { world: "u1l1.karel" },
  { world: "u1l1.beeper" },
  { world: "u1l1.world" },
  { world: "u1l1.wall" },
];
const karelConceptLabels = ["Karel", "Beeper", "World", "Wall"];

const u1l1: LessonFactory = () =>
  PictureDemo(karelConcepts(), karelConceptLabels);
const u1l2: LessonFactory = () =>
  PictureLesson(karelConcepts(), karelConceptLabels);

function buttons4(): Record<string, boolean> {
  return { move: true, turnLeft: true, putBeeper: true, pickBeeper: true };
}

const u2l1: LessonFactory = () =>
  ButtonLesson(
    { move: true, turnLeft: false, putBeeper: false, pickBeeper: false },
    "u2l1",
    ["move"],
  );
const u2l2: LessonFactory = () =>
  ButtonLesson(
    { move: true, turnLeft: false, putBeeper: false, pickBeeper: false },
    "u2l2",
    ["move", "move"],
  );
const u2l3: LessonFactory = () =>
  ButtonLesson(
    { move: false, turnLeft: true, putBeeper: false, pickBeeper: false },
    "u2l3",
    ["turnLeft"],
  );
const u2l4: LessonFactory = () =>
  ButtonLesson(
    { move: true, turnLeft: true, putBeeper: false, pickBeeper: false },
    "u2l4",
    ["move", "turnLeft", "move", "turnLeft", "move"],
  );
const u2l5: LessonFactory = () =>
  ButtonLesson(
    { move: false, turnLeft: false, putBeeper: true, pickBeeper: false },
    "u2l5",
    ["putBeeper"],
  );
const u2l6: LessonFactory = () =>
  ButtonLesson(buttons4(), "u2l6", ["move", "putBeeper", "move"]);
const u2l7: LessonFactory = () =>
  ButtonLesson(buttons4(), "u2l7", ["move", "pickBeeper", "move"]);
const u2l8: LessonFactory = () =>
  ButtonLesson(buttons4(), "u2l8", [
    "move",
    "pickBeeper",
    "turnLeft",
    "move",
    "turnLeft",
    "turnLeft",
    "turnLeft",
    "move",
    "putBeeper",
    "move",
  ]);

const u3l1: LessonFactory = () => IdeDemo("u3l1");
const u3l2: LessonFactory = () => IdeDemo("u3l2");
const u3l3: LessonFactory = () => IdeLesson("u3l3");
const u3l4: LessonFactory = () => IdeLesson("u3l4");
const u3l5: LessonFactory = () => IdeLesson("u3l5");
const u3l6: LessonFactory = () => IdeLesson("u3l6");

const u4l1: LessonFactory = () => IdeDemo("u4l1");
const u4l2: LessonFactory = () => IdeLesson("u4l2");

const u5l1: LessonFactory = () => IdeLesson("u5l1");

const u6l1: LessonFactory = () => IdeDemo("u6l1");
const u6l2: LessonFactory = () => IdeLesson("u6l2");

const u7l1: LessonFactory = () =>
  PictureLesson(
    [
      { world: "u7l1.north" },
      { world: "u7l1.south" },
      { world: "u7l1.east" },
      { world: "u7l1.west" },
    ],
    [
      "<code>facingNorth()</code>",
      "<code>facingSouth()</code>",
      "<code>facingEast()</code>",
      "<code>facingWest()</code>",
    ],
  );
const u7l2: LessonFactory = () =>
  PictureLesson(
    [
      { world: "u7l2.a" },
      { world: "u7l2.b" },
      { world: "u7l2.c" },
      { world: "u7l2.d" },
    ],
    [
      '<code>canMove("forward")</code>',
      "<code>notFacingEast()</code>",
      '<code>canMove("left")</code>',
      "<code>beepersPresent()</code>",
    ],
  );
const u7l3: LessonFactory = () =>
  PictureLesson(
    [
      { world: "u7l3.a" },
      { world: "u7l3.b" },
      { world: "u7l3.c" },
      { world: "u7l3.d" },
    ],
    [
      "<code>notFacingSouth()</code>",
      '<code>!canMove("forward")</code>',
      "<code>noBeepersPresent()</code>",
      '<code>canMove("right")</code>',
    ],
  );

const u8l1: LessonFactory = () => IdeDemo("u8l1");
const u8l2: LessonFactory = () =>
  IdeLesson("u8l2", {
    "u8l2.start.5x5": "u8l2.goal.5x5",
    u8l2: "u8l2.goal.8x8",
  });
const u8l3: LessonFactory = () =>
  IdeLesson("u8l3", {
    "u8l3.start.5x5": "u8l3.goal.5x5",
    u8l3: "u8l3.goal.8x8",
  });
const u8l4: LessonFactory = () => IdeDemo("u8l4");
const u8l5: LessonFactory = () =>
  IdeLesson("u8l5", {
    "u8l5.start.5x5": "u8l5.goal.5x5",
    u8l5: "u8l5.goal.8x8",
  });

const u9l1: LessonFactory = () => IdeDemo("u9l1");
const u9l2: LessonFactory = () =>
  IdeLesson("u9l2", {
    u9l2: "u9l2.goal",
    "u9l2.start.column2": "u9l2.goal.column2",
  });

const u10l1: LessonFactory = () => IdeDemo("u10l1");
const u10l2: LessonFactory = () => IdeDemo("u10l2");

const u11l1: LessonFactory = () => IdeDemo("u11l1");
const u11l2: LessonFactory = () => IdeDemo("u11l2");

const u12l1: LessonFactory = () =>
  IdeLesson("u12l1", {
    "u12l1.start.5x5": "u12l1.goal.5x5",
    u12l1: "u12l1.goal.7x7",
  });
